import React from "react";
import { Loader2, Calendar, Trash2, Send } from "lucide-react";
import { useLanguage } from "@/lib/LanguageContext";
import { dateLocale } from "@/lib/i18n";

const STATUS_BADGE = {
  published: "bg-emerald-100 text-emerald-700",
  scheduled: "bg-amber-100 text-amber-700",
  draft: "bg-eh-cream text-eh-muted",
};

function PostCard({ post, accounts, publishingId, onEdit, onPublish, onDelete, t }) {
  const { lang } = useLanguage();
  return (
    <div data-testid={`post-card-${post.id}`} className="bg-white rounded-2xl border border-eh-border-soft p-4 flex flex-col">
      {post.image_url && <img src={post.image_url} alt="" className="rounded-xl mb-3 aspect-square object-cover" />}
      <div className="flex gap-1 flex-wrap mb-2">
        {(post.platforms || []).map((pl) => (
          <span key={pl} className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-eh-cream text-eh-muted">{pl}</span>
        ))}
        <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ml-auto ${STATUS_BADGE[post.status] || STATUS_BADGE.draft}`}>{post.status}</span>
      </div>
      <p className="text-sm text-eh-ink line-clamp-4 mb-2">{post.content}</p>
      {post.scheduled_at && (
        <div className="text-xs text-eh-muted mb-2 flex items-center gap-1">
          <Calendar size={12} /> {new Date(post.scheduled_at).toLocaleString(dateLocale(lang))}
        </div>
      )}
      <div className="flex gap-1 mt-auto pt-2 border-t border-eh-border-soft">
        <button onClick={() => onEdit(post)} className="text-xs text-eh-ink hover:underline">{t("social.edit")}</button>
        {post.status !== "published" && accounts.length > 0 && (
          <button
            onClick={() => onPublish(post.id)}
            disabled={publishingId === post.id}
            data-testid={`publish-${post.id}`}
            className="ml-2 text-xs text-emerald-700 hover:bg-emerald-50 px-2 py-0.5 rounded flex items-center gap-1"
          >
            {publishingId === post.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} {t("social.publish")}
          </button>
        )}
        <button onClick={() => onDelete(post.id)} className="ml-auto text-rose-500 hover:bg-rose-50 p-1 rounded">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function PostsGrid({ posts, accounts, publishingId, onEdit, onPublish, onDelete, t }) {
  if (posts.length === 0) {
    return (
      <div className="text-center py-16 text-eh-muted bg-white rounded-2xl border border-eh-border-soft">
        {t("social.emptyPosts")}
      </div>
    );
  }
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {posts.map((p) => (
        <PostCard
          key={p.id}
          post={p}
          accounts={accounts}
          publishingId={publishingId}
          onEdit={onEdit}
          onPublish={onPublish}
          onDelete={onDelete}
          t={t}
        />
      ))}
    </div>
  );
}
